import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import ActivityReportSelector from "./ActivityReportSelector";

// Duración de la animación de cierre, para no cortar el deslizamiento.
const CLOSE_MS = 200;

export default function ActivityReportSelectorPanel({
  blocks,
  selected,
  totalCount,
  onToggle,
  onSetMany,
  onCancel,
  onGenerate,
}) {
  const [visible, setVisible] = useState(false);
  const closeButtonRef = useRef(null);

  const closeWith = useCallback((callback) => {
    setVisible(false);
    setTimeout(callback, CLOSE_MS);
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => setVisible(true));
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeWith(onCancel);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [closeWith, onCancel]);

  const count = selected.size;
  const panel = (
    <div
      className="fixed inset-0 z-[99999]"
      role="dialog"
      aria-modal="true"
      aria-label="Seleccionar preguntas del informe"
    >
      <div
        className={`fixed inset-0 bg-black/40 transition-opacity duration-200 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => closeWith(onCancel)}
      />

      <div
        className={`fixed top-0 right-0 h-full w-full sm:max-w-[460px] flex flex-col bg-gray-50 shadow-2xl transition-transform duration-200 ease-out ${
          visible ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="shrink-0 px-4 py-4 bg-white border-b border-gray-200 flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">
              Seleccionar preguntas
            </h2>
            <p className="text-xs text-gray-500">
              Elige qué entra en el informe
            </p>
          </div>
          <button
            ref={closeButtonRef}
            onClick={() => closeWith(onCancel)}
            aria-label="Cerrar panel de selección"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <ActivityReportSelector
            blocks={blocks}
            selected={selected}
            totalCount={totalCount}
            onToggle={onToggle}
            onSetMany={onSetMany}
          />
        </div>

        <div className="shrink-0 px-4 py-3 bg-white border-t border-gray-200">
          <p className="text-xs text-gray-500 mb-2">
            {count === 1
              ? "1 pregunta seleccionada"
              : `${count} preguntas seleccionadas`}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => closeWith(onCancel)}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => closeWith(onGenerate)}
              disabled={count === 0}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Generar informe
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
